import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // In a real app, this would call the admin API
      // For simulation, we'll return mock data
      const mockStats = {
        totalUsers: 142,
        totalFiles: 1258,
        totalTranslations: 892,
        activeSubscriptions: 24,
        revenue: 2345.67,
        systemHealth: 'Operational'
      };
      
      setStats(mockStats);
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken'); // Assuming admin has separate auth
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <nav className="admin-nav">
        <button 
          className={activeTab === 'overview' ? 'active' : ''} 
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={activeTab === 'users' ? 'active' : ''} 
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={activeTab === 'files' ? 'active' : ''} 
          onClick={() => setActiveTab('files')}
        >
          Files
        </button>
        <button 
          className={activeTab === 'billing' ? 'active' : ''} 
          onClick={() => setActiveTab('billing')}
        >
          Billing
        </button>
        <button 
          className={activeTab === 'system' ? 'active' : ''} 
          onClick={() => setActiveTab('system')}
        >
          System
        </button>
      </nav>

      <main className="admin-main">
        {activeTab === 'overview' && <OverviewTab stats={stats} />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'files' && <FilesTab />}
        {activeTab === 'billing' && <BillingTab />}
        {activeTab === 'system' && <SystemTab />}
      </main>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats }) => {
  return (
    <div className="admin-tab">
      <h2>System Overview</h2>
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{stats.totalUsers}</h3>
          <p>Total Users</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalFiles}</h3>
          <p>Total Files Processed</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalTranslations}</h3>
          <p>Total Translations</p>
        </div>
        <div className="stat-card">
          <h3>{stats.activeSubscriptions}</h3>
          <p>Active Subscriptions</p>
        </div>
        <div className="stat-card">
          <h3>€{stats.revenue?.toFixed(2)}</h3>
          <p>Revenue (Last 30 Days)</p>
        </div>
        <div className="stat-card">
          <h3>{stats.systemHealth}</h3>
          <p>System Health</p>
        </div>
      </div>

      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <table className="activity-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Action</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>john@example.com</td>
              <td>Uploaded new document</td>
              <td>2 minutes ago</td>
            </tr>
            <tr>
              <td>sarah@example.com</td>
              <td>Translation completed</td>
              <td>5 minutes ago</td>
            </tr>
            <tr>
              <td>mike@example.com</td>
              <td>Upgraded to Pro plan</td>
              <td>12 minutes ago</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Users Tab Component
const UsersTab = () => {
  const [users, setUsers] = useState([
    { id: 1, email: 'john@example.com', name: 'John Doe', plan: 'Pro', status: 'active', created: '2023-05-15' },
    { id: 2, email: 'sarah@example.com', name: 'Sarah Smith', plan: 'Free', status: 'active', created: '2023-06-20' },
    { id: 3, email: 'mike@example.com', name: 'Mike Johnson', plan: 'Enterprise', status: 'active', created: '2023-04-10' },
  ]);

  return (
    <div className="admin-tab">
      <h2>Manage Users</h2>
      <div className="table-controls">
        <input type="text" placeholder="Search users..." className="search-input" />
        <select className="filter-select">
          <option>All Plans</option>
          <option>Free</option>
          <option>Pro</option>
          <option>Enterprise</option>
        </select>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Plan</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id}>
              <td>{user.id}</td>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{user.plan}</td>
              <td><span className={`status-badge ${user.status}`}>{user.status}</span></td>
              <td>{user.created}</td>
              <td>
                <button className="action-btn view">View</button>
                <button className="action-btn edit">Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Files Tab Component
const FilesTab = () => {
  const [files] = useState([
    { id: 1, user: 'john@example.com', name: 'contract.pdf', size: '1.2 MB', status: 'completed', uploaded: '2023-05-15 10:30' },
    { id: 2, user: 'sarah@example.com', name: 'report.docx', size: '0.8 MB', status: 'processing', uploaded: '2023-05-15 09:15' },
    { id: 3, user: 'mike@example.com', name: 'manual.txt', size: '0.5 MB', status: 'completed', uploaded: '2023-05-14 16:45' },
  ]);

  return (
    <div className="admin-tab">
      <h2>Manage Files</h2>
      <div className="table-controls">
        <input type="text" placeholder="Search files..." className="search-input" />
        <select className="filter-select">
          <option>All Status</option>
          <option>Completed</option>
          <option>Processing</option>
          <option>Failed</option>
        </select>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>User</th>
            <th>File Name</th>
            <th>Size</th>
            <th>Status</th>
            <th>Uploaded</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {files.map(file => (
            <tr key={file.id}>
              <td>{file.id}</td>
              <td>{file.user}</td>
              <td>{file.name}</td>
              <td>{file.size}</td>
              <td><span className={`status-badge ${file.status}`}>{file.status}</span></td>
              <td>{file.uploaded}</td>
              <td>
                <button className="action-btn view">View</button>
                <button className="action-btn delete">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Billing Tab Component
const BillingTab = () => {
  const [payments] = useState([
    { id: 1, user: 'john@example.com', amount: '9.99', plan: 'Pro', status: 'paid', date: '2023-05-01' },
    { id: 2, user: 'mike@example.com', amount: '29.99', plan: 'Enterprise', status: 'paid', date: '2023-05-01' },
    { id: 3, user: 'sarah@example.com', amount: '0.00', plan: 'Free', status: 'N/A', date: '2023-04-15' },
  ]);

  return (
    <div className="admin-tab">
      <h2>Billing Overview</h2>
      <div className="stats-grid billing-stats">
        <div className="stat-card">
          <h3>€2,345.67</h3>
          <p>Monthly Revenue</p>
        </div>
        <div className="stat-card">
          <h3>24</h3>
          <p>Paying Users</p>
        </div>
        <div className="stat-card">
          <h3>15%</h3>
          <p>Conversion Rate</p>
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Payment ID</th>
            <th>User</th>
            <th>Amount</th>
            <th>Plan</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {payments.map(payment => (
            <tr key={payment.id}>
              <td>{payment.id}</td>
              <td>{payment.user}</td>
              <td>€{payment.amount}</td>
              <td>{payment.plan}</td>
              <td><span className={`status-badge ${payment.status}`}>{payment.status}</span></td>
              <td>{payment.date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// System Tab Component
const SystemTab = () => {
  const [systemInfo] = useState({
    version: '1.0.0',
    uptime: '41 days, 2 hours, 34 minutes',
    workers: 'Operational',
    r2: 'Operational',
    d1: 'Operational',
    queue: 'Operational',
    cache: 'Operational'
  });

  return (
    <div className="admin-tab">
      <h2>System Status</h2>
      <div className="system-info">
        <div className="info-item">
          <label>Application Version:</label>
          <span>{systemInfo.version}</span>
        </div>
        <div className="info-item">
          <label>Uptime:</label>
          <span>{systemInfo.uptime}</span>
        </div>
        <div className="info-grid">
          <div className="status-item">
            <span className="status-indicator operational"></span>
            <span>Workers: Operational</span>
          </div>
          <div className="status-item">
            <span className="status-indicator operational"></span>
            <span>R2 Storage: Operational</span>
          </div>
          <div className="status-item">
            <span className="status-indicator operational"></span>
            <span>D1 Database: Operational</span>
          </div>
          <div className="status-item">
            <span className="status-indicator operational"></span>
            <span>Queue System: Operational</span>
          </div>
          <div className="status-item">
            <span className="status-indicator operational"></span>
            <span>Cache Layer: Operational</span>
          </div>
        </div>
      </div>
      <div className="admin-actions">
        <button className="admin-btn primary">Clear Cache</button>
        <button className="admin-btn secondary">Run Maintenance</button>
        <button className="admin-btn danger">Restart Workers</button>
      </div>
    </div>
  );
};

export default AdminDashboard;